import React, { useEffect, useMemo, useRef, useState } from "react";
import RecordingPlayer, { type RecordingPlayerHandle } from "../ui/RecordingPlayer";
import TranscriptView from "../ui/TranscriptView";
import { funnelLabel } from "../../utils/funnelLabel";
import { formatDateTimeInTZ } from "../../lib/timezone";
import type { WordTime } from "../../hooks/useDialProof";
import { loadAllScenarios, courseTrackForRole, type CourseTrack } from "../../utils/scenarioForCall";
import { mergeAnchors, matchWords, matchFlat, type Anchor } from "../../utils/scriptMatch";

// The whole-course anchor corpus — the distinctive fixed phrases from every
// course script for the call's track, each tagged with the file it lives in.
// Built once per track (all scenarios loaded + merged + deduped), cached
// module-wide, and reused for every call's transcript highlight. No AI: pure
// approximate string matching. See utils/scriptMatch.ts.
// phone-setter: every phone-setter script, merged exactly as before.
// closer: the closer outbound docs, closerOnly=true (prospect lines never
// light up — the same mode calrev uses for the closer course).
const _anchorsByTrack = new Map<CourseTrack, Promise<Anchor[]>>();
const getCourseAnchors = (track: CourseTrack): Promise<Anchor[]> => {
  let p = _anchorsByTrack.get(track);
  if (!p) {
    p = loadAllScenarios(track).then((entries) =>
      track === "closer" ? mergeAnchors(entries, true) : mergeAnchors(entries)
    );
    _anchorsByTrack.set(track, p);
  }
  return p;
};

// =============================================================================
// callReviewParts — the shared master-detail dial call-review pieces
// =============================================================================
// ONE list-item + ONE detail panel, reused everywhere a dialer's calls get
// reviewed side-by-side: the proof drawer (DialProofModal) and the per-dialer
// connects panel (DialerConnectsPanel). Left = compact selectable call row;
// right = the selected call's recording + full-height TranscriptView (per-line
// m:ss stamps + click-to-seek + playhead highlight). Documented by /dialview.
//
// Both take a normalized ReviewCall so the same view renders regardless of which
// RPC produced the calls. testId props keep each host's existing Playwright
// selectors stable.
// =============================================================================

export interface ReviewCall {
  id: string;
  leadName: string | null;
  leadPhone?: string | null;
  /** Lead email — the key LeadTimelineModal opens by. Null when the RPC does not
   *  return it (e.g. get_dial_proof_rows), which disables the timeline link. */
  leadEmail?: string | null;
  funnelAtDial?: string | null;
  dialerName?: string | null;
  /** The CALLER's organization_members.role ("closer", "dialer", …). Picks the
   *  course track: "closer" → closer outbound course, anything else → phone
   *  setter. REQUIRED so every ReviewCall builder must decide it (null = unknown). */
  dialerRole: string | null;
  startedAt: string | null;
  durationSec: number | null;
  recordingUrl: string | null;
  /** Word-level transcript (null until the --word-times backfill runs). */
  words: WordTime[] | null;
  /** Flat whisper transcript text (null if the dial has no transcript at all). */
  flatTranscript: string | null;
}

// m:ss — 33 → "0:33"; NULL → "—" (voicemail / no-duration rows).
const formatDuration = (sec: number | null): string => {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

// One row in the LEFT call list. Compact: lead, funnel tag, dialer, time, length.
// Clicking it selects the call — its transcript fills the right panel.
export const CallReviewListItem: React.FC<{
  call: ReviewCall;
  timezone: string;
  selected: boolean;
  onSelect: () => void;
  testId?: string;
}> = ({ call, timezone, selected, onSelect, testId = "call-review-row" }) => (
  <button
    type="button"
    onClick={onSelect}
    data-testid={testId}
    aria-current={selected}
    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
      selected
        ? "border-ds-accent/40 bg-ds-accent/[0.07]"
        : "border-transparent hover:bg-black/[0.03]"
    }`}
  >
    <div className="flex items-center justify-between gap-2">
      <span className="token-13 text-glass truncate">
        {call.leadName || call.leadPhone || "Unknown lead"}
      </span>
      <span className="token-13m text-glass-secondary shrink-0">
        {formatDuration(call.durationSec)}
      </span>
    </div>
    <div className="flex items-center gap-1.5 mt-0.5">
      {funnelLabel(call.funnelAtDial) && (
        <span className="token-10 px-1.5 py-0.5 rounded bg-black/[0.05] text-glass-secondary">
          {funnelLabel(call.funnelAtDial)}
        </span>
      )}
      {call.dialerName && (
        <span className="token-11 text-glass-muted truncate">· {call.dialerName}</span>
      )}
    </div>
    <div className="token-11 text-glass-muted mt-0.5">
      {call.startedAt ? formatDateTimeInTZ(call.startedAt, timezone) : "—"}
    </div>
  </button>
);

// The RIGHT panel: one call, full height. Sticky header + recording player on top,
// the transcript scrolls beneath it at full height via the shared TranscriptView
// (word-line view + per-line m:ss + click-to-seek + playhead; flat fallback; then
// an empty note). `footer` renders under the header (e.g. an "open timeline" link).
export const CallReviewDetail: React.FC<{
  call: ReviewCall;
  timezone: string;
  testId?: string;
  footer?: React.ReactNode;
  emptyLabel?: string;
  /** Click a green (on-script) transcript span → teleport the course panel here. */
  onScriptJump?: (anchor: Anchor) => void;
  /** Open already seeked to this second (Course page examples). Plays from there. */
  startSec?: number;
  /** Start with Follow on (Course page word match: the transcript moves as they talk). */
  autoFollow?: boolean;
}> = ({
  call,
  timezone,
  testId = "call-review-detail",
  footer,
  emptyLabel = "No transcript for this call.",
  onScriptJump,
  startSec,
  autoFollow,
}) => {
  const playerRef = useRef<RecordingPlayerHandle | null>(null);
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  const [currentMs, setCurrentMs] = useState<number | null>(null);
  const words = call.words;

  // One seek per call. seekTo parks the target until the signed URL's metadata
  // lands, so this is safe on first paint.
  useEffect(() => {
    if (startSec == null || !call.recordingUrl) return;
    playerRef.current?.seekTo(startSec);
  }, [call.id, call.recordingUrl, startSec]);

  // FOLLOW: keep the word being said in view while the recording plays. Only a
  // human scroll (wheel, touch, scrollbar drag, scroll keys) turns it off; our
  // own scrollTo calls fire plain "scroll" events, which we never listen to.
  const [follow, setFollow] = useState(autoFollow ?? false);
  const wordEl = (box: HTMLElement): HTMLElement | null => {
    if (!words || currentMs == null) return null;
    let i = -1;
    for (let k = 0; k < words.length && words[k].start_ms <= currentMs; k++) i = k;
    if (i < 0) return null;
    // TranscriptView renders one span per word, in word order.
    return box.querySelectorAll<HTMLElement>('[data-testid="transcript-word"]')[i] ?? null;
  };
  useEffect(() => {
    const box = transcriptRef.current;
    if (!follow || !box) return;
    const el = wordEl(box);
    if (!el) return;
    const y = el.getBoundingClientRect().top - box.getBoundingClientRect().top;
    // Re-center only when the word leaves the middle band, so it doesn't jitter.
    if (y < box.clientHeight * 0.2 || y > box.clientHeight * 0.6) {
      box.scrollTo({ top: box.scrollTop + y - box.clientHeight * 0.35, behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [follow, currentMs]);
  const breakFollow = () => setFollow(false);
  // Space/Enter stay out: on a focused word they seek, not scroll.
  const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"]);

  // ...and bring the transcript to the same spot. Lines carry their m:ss stamp
  // as the first button's text; land on the last line that starts by the moment
  // (startSec + its 3s lead-in), a third of the way down so the lead-in shows.
  useEffect(() => {
    if (startSec == null || !words) return;
    const raf = requestAnimationFrame(() => {
      const box = transcriptRef.current;
      if (!box) return;
      let target: HTMLElement | null = null;
      box.querySelectorAll<HTMLElement>('[data-testid="transcript-line"]').forEach((line) => {
        const m = /^(\d+):(\d\d)$/.exec(line.querySelector("button")?.textContent?.trim() ?? "");
        if (m && Number(m[1]) * 60 + Number(m[2]) <= startSec + 3) target = line;
      });
      if (target) {
        // Measure against the box itself, so it works whatever the offsetParent is.
        const top =
          box.scrollTop +
          (target as HTMLElement).getBoundingClientRect().top -
          box.getBoundingClientRect().top -
          box.clientHeight / 3;
        box.scrollTo({ top: Math.max(0, top) });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [call.id, startSec, words]);

  // Course-script highlight (no AI). Load the anchor corpus once, then mark the
  // transcript spans that are near-verbatim from the call's course (phone-setter or closer).
  const track = courseTrackForRole(call.dialerRole);
  // Held with the track it was built for, so a track change shows no stale
  // highlights (derived, no reset effect — the repo lint bans setState-in-effect).
  const [anchorState, setAnchorState] = useState<{ track: CourseTrack; anchors: Anchor[] } | null>(
    null
  );
  useEffect(() => {
    let cancelled = false;
    getCourseAnchors(track)
      .then((a) => {
        if (!cancelled) setAnchorState({ track, anchors: a });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [track]);
  const anchors = anchorState?.track === track ? anchorState.anchors : null;
  const highlightWordIdx = useMemo(
    () => (anchors && words ? matchWords(words, anchors) : null),
    [anchors, words]
  );
  const highlightRanges = useMemo(
    () =>
      anchors && !words && call.flatTranscript ? matchFlat(call.flatTranscript, anchors) : null,
    [anchors, words, call.flatTranscript]
  );
  const matchCount = highlightWordIdx?.size ?? highlightRanges?.length ?? 0;

  return (
    <div
      className="flex flex-col h-full min-h-0"
      data-testid={testId}
      data-course-track={track}
      data-match-count={matchCount}
    >
      {/* Call header + recording — sticky above the scrolling transcript.
          Compact padding so the recording player rides higher and the
          transcript keeps the vertical space. */}
      <div className="px-5 pt-3 pb-2 border-b border-black/[0.06] shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="token-15 text-glass truncate">
              {call.leadName || call.leadPhone || "Unknown lead"}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {funnelLabel(call.funnelAtDial) && (
                <span className="token-10 px-1.5 py-0.5 rounded bg-black/[0.05] text-glass-secondary">
                  {funnelLabel(call.funnelAtDial)}
                </span>
              )}
              {call.dialerName && (
                <span className="token-11 text-glass-muted">· {call.dialerName}</span>
              )}
              <span className="token-11 text-glass-muted">
                · {call.startedAt ? formatDateTimeInTZ(call.startedAt, timezone) : "—"}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {footer}
            <span className="token-14 font-mono text-glass">
              {formatDuration(call.durationSec)}
            </span>
          </div>
        </div>

        {call.recordingUrl && (
          <div className="mt-2">
            <RecordingPlayer
              ref={playerRef}
              recordingUrl={call.recordingUrl}
              occurredAt={call.startedAt ?? undefined}
              durationSec={call.durationSec ?? undefined}
              onTimeUpdate={words ? (sec) => setCurrentMs(sec * 1000) : undefined}
              autoLoad
            />
          </div>
        )}
      </div>

      {/* Transcript — full remaining height, its own scroll (shared TranscriptView) */}
      <div
        ref={transcriptRef}
        data-testid="transcript-full"
        className="relative flex-1 min-h-0 overflow-y-auto px-5 py-4"
        onWheel={breakFollow}
        onTouchMove={breakFollow}
        onKeyDown={(e) => SCROLL_KEYS.has(e.key) && breakFollow()}
        // a press on the box itself (not a word/stamp) is the scrollbar
        onPointerDown={(e) => e.target === e.currentTarget && breakFollow()}
      >
        {words && call.recordingUrl && (
          <div className="sticky top-0 z-10 flex justify-end -mt-2 mb-1 pointer-events-none">
            <button
              type="button"
              data-testid="transcript-follow"
              aria-pressed={follow}
              onClick={() => setFollow((f) => !f)}
              title={follow ? "Following the recording. Scroll to stop." : "Keep the word being said in view"}
              className={`pointer-events-auto anim-tap inline-flex items-center gap-1.5 rounded-full px-3 py-1 token-11 font-semibold! shadow-sm transition-colors ${
                follow
                  ? "bg-ds-accent text-white"
                  : "bg-white/95 text-ds-accent border border-ds-accent/30 hover:bg-ds-accent/10"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${follow ? "bg-white anim-live-pulse" : "bg-ds-accent"}`}
                aria-hidden
              />
              {follow ? "Following" : "Follow"}
            </button>
          </div>
        )}
        {matchCount > 0 && (
          <p
            className="token-11 text-glass-muted mb-3 flex items-center gap-1.5"
            data-testid="script-match-legend"
          >
            <span className="inline-block w-3.5 h-3.5 rounded-sm bg-emerald-400/25 shadow-[inset_0_-2px_0_rgba(16,185,129,0.55)]" />
            {onScriptJump
              ? "Green = word-for-word from the course. Click it to jump to that spot in the script."
              : "Green = word-for-word from the course script."}{" "}
            A dark line is not proof of a miss (the transcript can garble).
          </p>
        )}
        <TranscriptView
          words={words}
          flatTranscript={call.flatTranscript}
          currentMs={currentMs}
          onSeekMs={(ms) => playerRef.current?.seekTo(ms / 1000)}
          highlightWordIdx={highlightWordIdx}
          highlightRanges={highlightRanges}
          onScriptJump={onScriptJump}
          emptyLabel={emptyLabel}
        />
      </div>
    </div>
  );
};
